import type { SupportedLocale } from '../i18n';
import { getOrderLocalityDisplayName } from './order-locality';
import type { Order } from '../types/order';

export interface OrderDeliveryCoordinates {
  readonly latitude: number;
  readonly longitude: number;
}

export interface OrderDeliveryDisplay {
  readonly recipientName: string | null;
  readonly phone: string | null;
  readonly locality: string | null;
  readonly postalCode: string | null;
  readonly countryCode: string | null;
  readonly streetLines: readonly string[];
  readonly destination: string;
  readonly detailLines: readonly string[];
  readonly clipboardText: string;
  readonly coordinates: OrderDeliveryCoordinates | null;
  readonly coordinatesText: string | null;
  readonly hasStreetAddress: boolean;
  readonly hasAnyDeliveryInfo: boolean;
}

function cleanText(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

function compactParts(parts: readonly (string | null | undefined)[]): string[] {
  return parts.map((part) => cleanText(part)).filter((part): part is string => part !== null);
}

function formatLocalityPostal(order: Order, locale: SupportedLocale): string | null {
  const locality = getOrderLocalityDisplayName(order, locale);
  const region = cleanText(order.address_region_snapshot);
  const postalCode = cleanText(order.address_postal_code_snapshot);
  const place = compactParts([locality, region]).join(', ');
  if (!place) return postalCode;
  return compactParts([place, postalCode]).join(' ');
}

export function buildOrderDeliveryDisplay(
  order: Order,
  locale: SupportedLocale,
): OrderDeliveryDisplay {
  if (order.delivery_method === 'pickup') throw new Error('DELIVERY_FULFILLMENT_ONLY');
  const recipientName =
    cleanText(order.address_recipient_snapshot) ??
    cleanText(order.customer_name_snapshot) ??
    order.customer_name_snapshot;
  const phone = cleanText(order.address_phone_snapshot) ?? cleanText(order.customer_phone_snapshot);
  const locality = getOrderLocalityDisplayName(order, locale);
  const postalCode = cleanText(order.address_postal_code_snapshot);
  const countryCode = cleanText(order.address_country_code_snapshot);
  const streetLines = compactParts([order.address_line_1_snapshot, order.address_line_2_snapshot]);
  const destination = compactParts([locality, order.address_region_snapshot]).join(', ');
  const localityPostal = formatLocalityPostal(order, locale);
  const coordinates =
    order.delivery_latitude !== null && order.delivery_longitude !== null
      ? {
          latitude: order.delivery_latitude,
          longitude: order.delivery_longitude,
        }
      : null;
  const coordinatesText = coordinates ? `${coordinates.latitude}, ${coordinates.longitude}` : null;
  const detailLines = compactParts([
    recipientName,
    ...streetLines,
    localityPostal,
    countryCode,
    phone,
  ]);

  return {
    recipientName,
    phone,
    locality,
    postalCode,
    countryCode,
    streetLines,
    destination,
    detailLines,
    clipboardText: detailLines.join('\n'),
    coordinates,
    coordinatesText,
    hasStreetAddress: streetLines.length > 0,
    hasAnyDeliveryInfo: detailLines.length > 0 || coordinates !== null,
  };
}
